import React, { useEffect, useState, useCallback } from "react";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// FoodFormBuilderTab — Donations private workspace tab.
// • Editable form title + description
// • Editable built-in field labels + secondary-phone required toggle
// • Add/edit/reorder/remove "Foods You Do Not Consume" options
// • Edit/add/remove Dependents radio options
// • Add new custom fields (short text / long text / radio / checkbox)
// • Live preview of how the public form will render after save
// • Super Admin only: version history with revert + diff
// ============================================================
export default function FoodFormBuilderTab({ isSuperAdmin, canManage }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await kcFetch(`${API_BASE}/public/food-form/schema`);
      if (r.ok) setSchema(await r.json());
    } catch (_) {}
    setLoading(false);
  }, []);
  const loadHistory = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await kcFetch(`${API_BASE}/donations/food-form/history`, { headers: { ...authHeaders() } });
      if (r.ok) setHistory(await r.json());
    } catch (_) {}
  }, [isSuperAdmin]);
  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  if (!canManage) {
    return <div className="don-empty" data-testid="ffb-locked" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="lock" size={16} /> Only Super Admins and the Donations Department Leader can edit the Food Contribution Registration form.</div>;
  }
  if (loading || !schema) return <div className="don-empty">Loading form structure…</div>;

  const set = (path, val) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
    cur[parts[parts.length - 1]] = val;
    return next;
  });

  const updateList = (path, mutate) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
    const last = parts[parts.length - 1];
    cur[last] = mutate([...(cur[last] || [])]);
    return next;
  });

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/donations/food-form/schema`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ formSchema: schema, changeNote: changeNote.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Save failed");
      }
      const j = await r.json();
      setSchema(j.schema);
      setChangeNote("");
      setSavedAt(Date.now());
      loadHistory();
    } catch (e) { setError(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const revert = async (h) => {
    if (!window.confirm(`Revert the form to the version saved by ${h.byUserName || "Unknown"} on ${new Date(h.at).toLocaleString()}?`)) return;
    setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/donations/food-form/history/${h.id}/revert`, {
        method: "POST",
        headers: { ...authHeaders() },
      });
      if (!r.ok) throw new Error("Revert failed");
      const j = await r.json();
      setSchema(j.schema);
      loadHistory();
    } catch (e) { setError(e.message || "Revert failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="food-form-builder">
      <div className="ffb-toolbar">
        <div className="ffb-toolbar-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="clipboard" size={16} /> Food Contribution Registration — Form Builder</div>
        {isSuperAdmin && (
          <button className="btn btn-outline btn-sm" data-testid="ffb-toggle-history" onClick={() => setShowHistory(v => !v)}>
            {showHistory ? "Hide" : "Show"} version history ({history.length})
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="form-section-title mb-12">Title &amp; Description</div>
        <div className="form-group mb-14">
          <label>Form Title</label>
          <input data-testid="ffb-title" value={schema.title || ""} onChange={e => set("title", e.target.value)} maxLength={120} />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea data-testid="ffb-description" value={schema.description || ""} onChange={e => set("description", e.target.value)} maxLength={400} rows={2} />
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="form-section-title mb-12">Built-in Field Labels</div>
        {[
          { id: "firstName", forced: true },
          { id: "lastName", forced: true },
          { id: "address", forced: true },
          { id: "phonePrimary", forced: true },
          { id: "phoneSecondary", forced: false },
        ].map(({ id, forced }) => {
          const f = schema.fields?.[id] || {};
          return (
            <div key={id} className="ffb-built-row">
              <input
                className="ffb-built-label"
                data-testid={`ffb-label-${id}`}
                value={f.label || ""}
                onChange={e => set(`fields.${id}.label`, e.target.value)}
                placeholder="Field label"
              />
              <label className="ffb-toggle">
                <input
                  type="checkbox"
                  data-testid={`ffb-required-${id}`}
                  checked={!!f.required}
                  disabled={forced}
                  onChange={e => set(`fields.${id}.required`, e.target.checked)}
                />
                <span>Required</span>
              </label>
              {!forced && (
                <label className="ffb-toggle">
                  <input
                    type="checkbox"
                    data-testid={`ffb-enabled-${id}`}
                    checked={f.enabled !== false}
                    onChange={e => set(`fields.${id}.enabled`, e.target.checked)}
                  />
                  <span>Shown on form</span>
                </label>
              )}
              {forced && <span className="ffb-locked-pill">REQUIRED</span>}
            </div>
          );
        })}
      </div>

      <OptionsCard
        title="Dependents (radio group)"
        labelPath="dependents.label"
        optionsPath="dependents.options"
        schema={schema}
        set={set}
        updateList={updateList}
        testIdPrefix="ffb-dep"
      />

      <OptionsCard
        title='"Foods You Do Not Consume" (multi-select checkboxes)'
        labelPath="avoidFoods.label"
        optionsPath="avoidFoods.options"
        schema={schema}
        set={set}
        updateList={updateList}
        testIdPrefix="ffb-foods"
      />

      <CustomFieldsCard schema={schema} setSchema={setSchema} />

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="form-section-title mb-12">Save</div>
        <div className="form-group mb-14">
          <label>Change note (optional)</label>
          <input data-testid="ffb-change-note" value={changeNote} onChange={e => setChangeNote(e.target.value)} placeholder="Briefly describe what you changed" maxLength={200} />
        </div>
        {error && <div className="ffb-error" data-testid="ffb-error">{error}</div>}
        {savedAt > 0 && !error && <div className="ffb-saved" data-testid="ffb-saved" style={{ display: "flex", alignItems: "center", gap: 6 }}><CustomIcon name="check" size={14} /> Form updated. The public form will now show the new structure.</div>}
        <button className="btn btn-gold" data-testid="ffb-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Form"}</button>
      </div>

      {showHistory && isSuperAdmin && (
        <div className="card" style={{ padding: 18, marginBottom: 14 }} data-testid="ffb-history-panel">
          <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="document-edit" size={16} /> Version History (Super Admin only)</div>
          {history.length === 0 ? (
            <div className="don-empty">No saved versions yet.</div>
          ) : (
            <div className="ffb-history-list">
              {history.map(h => (
                <div key={h.id} className="ffb-history-row" data-testid={`ffb-history-row-${h.id}`}>
                  <div className="ffb-history-meta">
                    <div className="ffb-history-when">{new Date(h.at).toLocaleString()}</div>
                    <div className="ffb-history-who">{h.byUserName || "Unknown user"}{h.isRevert ? " · reverted" : ""}</div>
                    {h.note && <div className="ffb-history-note">"{h.note}"</div>}
                  </div>
                  <button className="btn btn-outline btn-sm" data-testid={`ffb-revert-${h.id}`} onClick={() => revert(h)}>
                    Revert to before this change
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{CSS}</style>
    </div>
  );
}

function OptionsCard({ title, labelPath, optionsPath, schema, set, updateList, testIdPrefix }) {
  const label = labelPath.split(".").reduce((o, p) => o?.[p], schema) || "";
  const options = optionsPath.split(".").reduce((o, p) => o?.[p], schema) || [];

  const move = (idx, dir) => updateList(optionsPath, arr => {
    const next = idx + dir;
    if (next < 0 || next >= arr.length) return arr;
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    return arr;
  });

  return (
    <div className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div className="form-section-title mb-12">{title}</div>
      <div className="form-group mb-14">
        <label>Group label</label>
        <input data-testid={`${testIdPrefix}-label`} value={label} onChange={e => set(labelPath, e.target.value)} />
      </div>
      {options.map((opt, i) => (
        <div key={i} className="ffb-opt-row">
          <input data-testid={`${testIdPrefix}-opt-${i}`} value={opt} onChange={e => updateList(optionsPath, arr => { arr[i] = e.target.value; return arr; })} />
          <button className="btn btn-sm btn-outline" data-testid={`${testIdPrefix}-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
          <button className="btn btn-sm btn-outline" data-testid={`${testIdPrefix}-down-${i}`} disabled={i === options.length - 1} onClick={() => move(i, 1)}>↓</button>
          <button className="btn btn-sm btn-danger" data-testid={`${testIdPrefix}-remove-${i}`} onClick={() => updateList(optionsPath, arr => { arr.splice(i, 1); return arr; })} aria-label="Remove option"><CustomIcon name="close" size={12} /></button>
        </div>
      ))}
      <button className="btn btn-outline btn-sm" data-testid={`${testIdPrefix}-add`} onClick={() => updateList(optionsPath, arr => [...arr, "New option"])}>+ Add option</button>
    </div>
  );
}

function CustomFieldsCard({ schema, setSchema }) {
  const fields = Array.isArray(schema.customFields) ? schema.customFields : [];
  const update = (idx, patch) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    next.customFields[idx] = { ...next.customFields[idx], ...patch };
    return next;
  });
  const remove = (idx) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    next.customFields.splice(idx, 1);
    return next;
  });
  const add = () => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    next.customFields = next.customFields || [];
    next.customFields.push({
      id: "cf_" + Math.random().toString(36).slice(2, 8),
      label: "New optional field",
      type: "short_text",
      required: false,
      options: [],
    });
    return next;
  });
  const move = (idx, dir) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    const j = idx + dir;
    if (j < 0 || j >= next.customFields.length) return next;
    [next.customFields[idx], next.customFields[j]] = [next.customFields[j], next.customFields[idx]];
    return next;
  });
  const updateOpts = (idx, fn) => setSchema(s => {
    const next = JSON.parse(JSON.stringify(s));
    next.customFields[idx].options = fn([...(next.customFields[idx].options || [])]);
    return next;
  });

  return (
    <div className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div className="form-section-title mb-12">Custom Optional Fields</div>
      <div style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 14 }}>
        Add brand-new fields beyond the built-ins. Pick a type: short text, long text, single-select radio, or multi-select checkbox.
      </div>
      {fields.length === 0 && <div className="don-empty" style={{ marginBottom: 10 }}>No custom fields yet.</div>}
      {fields.map((f, i) => (
        <div key={f.id} className="ffb-cf" data-testid={`ffb-cf-${f.id}`}>
          <div className="ffb-cf-head">
            <input data-testid={`ffb-cf-label-${f.id}`} value={f.label} onChange={e => update(i, { label: e.target.value })} placeholder="Field label" />
            <select data-testid={`ffb-cf-type-${f.id}`} value={f.type} onChange={e => update(i, { type: e.target.value, options: ["radio", "checkbox"].includes(e.target.value) ? (f.options || ["Option 1", "Option 2"]) : [] })}>
              <option value="short_text">Short text</option>
              <option value="long_text">Long text</option>
              <option value="radio">Radio (single-select)</option>
              <option value="checkbox">Checkbox (multi-select)</option>
            </select>
            <label className="ffb-toggle">
              <input type="checkbox" data-testid={`ffb-cf-req-${f.id}`} checked={!!f.required} onChange={e => update(i, { required: e.target.checked })} />
              <span>Required</span>
            </label>
            <button className="btn btn-sm btn-outline" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button className="btn btn-sm btn-outline" disabled={i === fields.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button className="btn btn-sm btn-danger" data-testid={`ffb-cf-remove-${f.id}`} onClick={() => remove(i)} aria-label="Remove field"><CustomIcon name="close" size={12} /></button>
          </div>
          {(f.type === "radio" || f.type === "checkbox") && (
            <div style={{ marginTop: 10, paddingLeft: 14, borderLeft: "2px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)" }}>
              {(f.options || []).map((o, j) => (
                <div key={j} className="ffb-opt-row">
                  <input value={o} onChange={e => updateOpts(i, arr => { arr[j] = e.target.value; return arr; })} />
                  <button className="btn btn-sm btn-danger" onClick={() => updateOpts(i, arr => { arr.splice(j, 1); return arr; })} aria-label="Remove option"><CustomIcon name="close" size={12} /></button>
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => updateOpts(i, arr => [...arr, "New option"])}>+ Add option</button>
            </div>
          )}
        </div>
      ))}
      <button className="btn btn-outline btn-sm" data-testid="ffb-cf-add" onClick={add}>+ Add custom field</button>
    </div>
  );
}

const CSS = `
.ffb-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.ffb-toolbar-title { font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.06em; text-transform: uppercase; }
.ffb-built-row { display: grid; grid-template-columns: 1.5fr auto auto auto; gap: 10px; align-items: center; padding: 8px 0; border-bottom: 1px dashed color-mix(in srgb, var(--kc-accent) 10%, transparent); }
.ffb-built-row:last-child { border-bottom: none; }
.ffb-built-label { background: #141414; color: #FFF; border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 8px; padding: 8px 12px; font-size: 13px; font-family: 'Montserrat',sans-serif; }
.ffb-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: var(--kc-sub); letter-spacing: 0.05em; text-transform: uppercase; font-weight: 700; cursor: pointer; }
.ffb-toggle input { accent-color: var(--kc-accent); }
.ffb-locked-pill { font-size: 10px; padding: 3px 8px; border-radius: 999px; background: color-mix(in srgb, var(--kc-accent) 15%, transparent); color: var(--kc-accent); font-weight: 700; letter-spacing: 0.08em; }
.ffb-opt-row { display: grid; grid-template-columns: 1fr auto auto auto; gap: 6px; align-items: center; margin-bottom: 6px; }
.ffb-opt-row input { background: #141414; color: #FFF; border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 8px; padding: 7px 11px; font-size: 13px; font-family: 'Montserrat',sans-serif; }
.ffb-cf { background: rgba(255,255,255,0.02); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); border-radius: 10px; padding: 12px; margin-bottom: 10px; }
.ffb-cf-head { display: grid; grid-template-columns: 2fr 160px auto auto auto auto; gap: 8px; align-items: center; }
.ffb-cf-head input, .ffb-cf-head select { background: #141414; color: #FFF; border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 8px; padding: 7px 11px; font-size: 13px; font-family: 'Montserrat',sans-serif; }
.ffb-error { background: rgba(255,107,107,0.10); border: 1px solid rgba(255,107,107,0.45); color: #ff9b9b; padding: 8px 12px; border-radius: 8px; font-size: 13px; margin-bottom: 10px; }
.ffb-saved { background: rgba(74,222,128,0.12); border: 1px solid rgba(74,222,128,0.40); color: #4ade80; padding: 8px 12px; border-radius: 8px; font-size: 13px; margin-bottom: 10px; }
.ffb-history-list { display: flex; flex-direction: column; gap: 8px; }
.ffb-history-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 10px 14px; background: rgba(255,255,255,0.02); border: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); border-radius: 8px; }
.ffb-history-meta { font-size: 12px; }
.ffb-history-when { color: var(--kc-text); font-weight: 600; }
.ffb-history-who { color: var(--kc-sub); margin-top: 2px; }
.ffb-history-note { color: var(--kc-accent); font-style: italic; margin-top: 4px; }
@media (max-width: 760px) {
  .ffb-built-row, .ffb-cf-head { grid-template-columns: 1fr; }
}
`;
